import { useState } from 'react';

interface Example {
  problem: string;
  steps: string[];
}

interface PracticeQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface Lesson {
  id: number;
  title: string;
  vocabulary: { term: string; definition: string }[];
  conceptExplanation: string;
  examples: Example[];
  practiceQuestions: PracticeQuestion[];
  teacherTip: string;
}

interface LessonCardProps {
  lesson: Lesson;
}

export default function LessonCard({ lesson }: LessonCardProps) {
  const [expandedExamples, setExpandedExamples] = useState<number[]>([]);
  const [exampleSteps, setExampleSteps] = useState<{ [key: number]: number }>({});
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: number }>({});
  const [showResults, setShowResults] = useState(false);

  const toggleExample = (index: number) => {
    if (expandedExamples.includes(index)) {
      setExpandedExamples(expandedExamples.filter(i => i !== index));
      setExampleSteps({ ...exampleSteps, [index]: 0 });
    } else {
      setExpandedExamples([...expandedExamples, index]);
      setExampleSteps({ ...exampleSteps, [index]: 0 });
    }
  };

  const revealNextStep = (exampleIndex: number) => {
    const currentStep = exampleSteps[exampleIndex] || 0;
    const maxSteps = lesson.examples[exampleIndex].steps.length;
    if (currentStep < maxSteps) {
      setExampleSteps({ ...exampleSteps, [exampleIndex]: currentStep + 1 });
    }
  };

  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    if (!showResults) {
      setSelectedAnswers({ ...selectedAnswers, [questionIndex]: answerIndex });
    }
  };

  const checkAnswers = () => {
    setShowResults(true);
  };

  const resetPractice = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };

  const calculateScore = () => {
    let correct = 0;
    lesson.practiceQuestions.forEach((q, index) => {
      if (selectedAnswers[index] === q.correctAnswer) {
        correct++;
      }
    });
    return correct;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
      <h3 className="text-3xl font-bold text-gray-900 mb-6">{lesson.title}</h3>

      {/* Vocabulary */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-purple-600 mb-4 flex items-center gap-2">
          <i className="ri-book-open-line"></i>
          Key Vocabulary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lesson.vocabulary.map((item, index) => (
            <div key={index} className="bg-purple-50 rounded-lg p-4">
              <span className="font-bold text-purple-700">{item.term}:</span>
              <span className="text-gray-700 ml-2">{item.definition}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Concept Explanation */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-purple-600 mb-4 flex items-center gap-2">
          <i className="ri-lightbulb-line"></i>
          Understanding the Concept
        </h4>
        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-6">
          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{lesson.conceptExplanation}</p>
        </div>
      </div>

      {/* Worked Examples */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-purple-600 mb-4 flex items-center gap-2">
          <i className="ri-pencil-ruler-2-line"></i>
          Worked Examples
        </h4>
        <div className="space-y-4">
          {lesson.examples.map((example, index) => (
            <div key={index} className="border-2 border-purple-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleExample(index)}
                className="w-full bg-purple-100 hover:bg-purple-200 transition-colors duration-300 p-4 flex items-center justify-between cursor-pointer"
              >
                <span className="font-bold text-gray-900">Example {index + 1}: {example.problem}</span>
                <i className={`ri-arrow-${expandedExamples.includes(index) ? 'up' : 'down'}-s-line text-2xl text-purple-600`}></i>
              </button>
              {expandedExamples.includes(index) && (
                <div className="p-6 bg-white">
                  <div className="space-y-3 mb-4">
                    {example.steps.slice(0, exampleSteps[index] || 0).map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-start gap-3 bg-purple-50 p-3 rounded-lg">
                        <span className="font-bold text-purple-600 flex-shrink-0">Step {stepIndex + 1}:</span>
                        <span className="text-gray-800">{step}</span>
                      </div>
                    ))}
                  </div>
                  {(exampleSteps[index] || 0) < example.steps.length && (
                    <button
                      onClick={() => revealNextStep(index)}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Reveal Next Step
                    </button>
                  )}
                  {(exampleSteps[index] || 0) === example.steps.length && (
                    <div className="flex items-center gap-2 text-green-600 font-semibold">
                      <i className="ri-checkbox-circle-fill text-2xl"></i>
                      <span>Complete!</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Practice Questions */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-purple-600 mb-4 flex items-center gap-2">
          <i className="ri-question-line"></i>
          Practice Questions
        </h4>
        <div className="space-y-6">
          {lesson.practiceQuestions.map((question, qIndex) => (
            <div key={qIndex} className="bg-purple-50 rounded-lg p-6">
              <p className="font-bold text-gray-900 mb-4">{qIndex + 1}. {question.question}</p>
              <div className="space-y-2 mb-4">
                {question.options.map((option, oIndex) => {
                  const isSelected = selectedAnswers[qIndex] === oIndex;
                  const isCorrect = oIndex === question.correctAnswer;
                  const showCorrect = showResults && isCorrect;
                  const showIncorrect = showResults && isSelected && !isCorrect;

                  return (
                    <button
                      key={oIndex}
                      onClick={() => handleAnswerSelect(qIndex, oIndex)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-300 cursor-pointer ${
                        showCorrect
                          ? 'bg-green-100 border-green-500'
                          : showIncorrect
                          ? 'bg-red-100 border-red-500'
                          : isSelected
                          ? 'bg-purple-100 border-purple-500'
                          : 'bg-white border-purple-200 hover:border-purple-400'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-gray-800">{option}</span>
                        {showCorrect && <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>}
                        {showIncorrect && <i className="ri-close-circle-fill text-red-600 text-xl"></i>}
                      </div>
                    </button>
                  );
                })}
              </div>
              {showResults && (
                <div className="bg-white rounded-lg p-4 border-l-4 border-purple-500">
                  <p className="text-sm font-semibold text-purple-700 mb-1">Explanation:</p>
                  <p className="text-gray-700">{question.explanation}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-4 mt-6">
          {!showResults ? (
            <button
              onClick={checkAnswers}
              disabled={Object.keys(selectedAnswers).length !== lesson.practiceQuestions.length}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-bold transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Check My Answers
            </button>
          ) : (
            <>
              <div className="flex items-center gap-3 bg-purple-100 px-6 py-3 rounded-lg">
                <i className="ri-trophy-line text-3xl text-purple-600"></i>
                <div>
                  <p className="font-bold text-gray-900">Your Score</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {calculateScore()} / {lesson.practiceQuestions.length}
                  </p>
                </div>
              </div>
              <button
                onClick={resetPractice}
                className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-bold transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>

      {/* Teacher's Tip */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-6 border-l-4 border-amber-500">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0">
            <i className="ri-user-smile-line text-white text-2xl"></i>
          </div>
          <div>
            <p className="font-bold text-gray-900 mb-2">Teacher's Tip from Mr. Augustine</p>
            <p className="text-gray-700 leading-relaxed">{lesson.teacherTip}</p>
          </div>
        </div>
      </div>
    </div>
  );
}